import { isQueryOnlyNavigation } from "./navigationTransition";

/**
 * View Next/Previous on a category lands the shopper once at the top of the
 * catalog grid (pages/store/category/category.vue). The router answered that
 * push with its own {top: 0}: it cancelled the catalog scroll and left every
 * measured transition at the document top, 216 px (desktop) / 294 px (mobile)
 * above the grid.
 *
 * The category claims the push's target URL right before router.push, and
 * the router's scrollBehavior consumes the claim and leaves the scroll to the
 * page. The claim is one-shot and covers only a query-only navigation to
 * exactly that URL; the router clears it on every navigation it scrolls, so
 * sort and filter changes, the manufacturer and search pagers and
 * Back/Forward keep their own scroll behaviour.
 */

type RouteLike = { path?: string; fullPath?: string } | null | undefined;

let claimedFullPath: string | null = null;

/** The category scrolls the next push to `fullPath` itself. */
export const claimCatalogPageScroll = (fullPath: string) => {
  claimedFullPath = fullPath ? String(fullPath) : null;
};

/** Drops an unused claim (the push failed, so the router never consumed it). */
export const releaseCatalogPageScroll = () => {
  claimedFullPath = null;
};

/**
 * For the router: true when the category owns the scroll of this navigation.
 * Always clears the claim, so it can never apply to a later navigation.
 */
export const consumeCatalogPageScrollClaim = (
  to: RouteLike,
  from: RouteLike
): boolean => {
  const claimed = claimedFullPath;
  claimedFullPath = null;
  return (
    !!claimed &&
    claimed === String(to?.fullPath || "") &&
    isQueryOnlyNavigation(String(from?.path || ""), String(to?.path || ""))
  );
};

/*
 * Back/Forward between pages of one listing (?page=3 -> ?page=2) restores the
 * position the shopper left that page at, as soon as its products are on
 * screen. Three listeners used to disagree about it:
 *   - create-app's popstate flag runs after vue-router's own listener, and the
 *     whole query-only navigation completes in between, so its scrollBehavior
 *     took the visit for a push and jumped to the document top;
 *   - the category's history path then scrolled to the catalog top;
 *   - entry-client's history restore waited up to 8 s for "replaced content",
 *     which never comes when the path stays the same, then jumped.
 * Now entry-client's popstate listener (registered before vue-router's) notes
 * the visit here; the category's route watcher takes the note, claims the
 * scroll (claimCatalogPageScroll, so the router stays put), commits the page
 * without scrolling and marks it rendered; entry-client restores the saved
 * position on that mark, in the frame the products first paint.
 */

let historyVisitTarget: string | null = null;

/** entry-client's popstate listener: a Back/Forward visit to `fullPath`. */
export const noteCatalogHistoryVisit = (fullPath: string) => {
  historyVisitTarget = fullPath ? String(fullPath) : null;
};

/** router.beforeEach of a navigation that is not a Back/Forward visit. */
export const clearCatalogHistoryVisit = () => {
  historyVisitTarget = null;
};

/**
 * For the category's route watcher: the URL of the Back/Forward visit in
 * progress, or null. One-shot, so it can never apply to a later navigation.
 */
export const takeCatalogHistoryVisit = (): string | null => {
  const target = historyVisitTarget;
  historyVisitTarget = null;
  return target;
};

export const CATALOG_HISTORY_PAGE_RENDERED_EVENT =
  "vf-catalog-history-page-rendered";

const RENDERED_PATH_KEY = "vfCatalogHistoryRenderedPath";

/** The category: the target page of a Back/Forward visit is on screen. */
export const markCatalogHistoryPageRendered = (fullPath: string) => {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  document.documentElement.dataset[RENDERED_PATH_KEY] = String(fullPath || "");
  window.dispatchEvent(
    new CustomEvent(CATALOG_HISTORY_PAGE_RENDERED_EVENT, {
      detail: { fullPath: String(fullPath || "") },
    })
  );
};

/** entry-client: true once the category marked `fullPath` as rendered. */
export const isCatalogHistoryPageRendered = (fullPath: string) =>
  typeof document !== "undefined" &&
  !!fullPath &&
  document.documentElement.dataset[RENDERED_PATH_KEY] === String(fullPath);

/** entry-client's popstate listener: no earlier mark counts for this visit. */
export const clearCatalogHistoryPageRendered = () => {
  if (typeof document === "undefined") return;
  delete document.documentElement.dataset[RENDERED_PATH_KEY];
};

/** Gap kept between the visible header and the top of the catalog grid. */
export const CATALOG_TOP_GAP_PX = 16;

/**
 * Document offset that puts the catalog top just below the visible header.
 * `headerVisibleHeight` is the raw --vf-header-visible-height value ("76px").
 */
export const catalogTopScrollOffset = ({
  catalogTop,
  scrollY,
  headerVisibleHeight,
}: {
  catalogTop: number;
  scrollY: number;
  headerVisibleHeight: string;
}) => {
  const header = Number(String(headerVisibleHeight || "").replace("px", ""));
  const offset =
    (Number(catalogTop) || 0) +
    (Number(scrollY) || 0) -
    (Number.isFinite(header)
      ? header + CATALOG_TOP_GAP_PX
      : CATALOG_TOP_GAP_PX);
  return Math.max(0, offset);
};
